import React from "react";
import { motion } from "framer-motion";
import { Shield, Leaf, Mountain, Sparkles } from "lucide-react";
import { useTheme } from "../../context/ThemeContext";
import { getTierNames } from "../../utils/tierNames";
import { sectionBgColor } from "../../utils/themeBg";

const PILLAR_ICONS = [Leaf, Shield, Mountain, Sparkles];

const DEFAULT_BRAND = {
  label: "La filosofia",
  title: "La forza del leone.",
  title_accent: "L'equilibrio della natura.",
  paragraph1:
    "LEOVERA nasce dall'incontro di due forze: la determinazione del leone e la precisione della natura. Selezioniamo le formulazioni più avanzate dei brand italiani di riferimento — Anderson, Eurosup e Prolabs — e le portiamo a chi non si accontenta.",
  pillars: [
    { title: "Naturale", desc: "Materie prime selezionate, formulazioni pulite, zero compromessi sulla qualità degli ingredienti." },
    { title: "Certificato", desc: "Produzione GMP, brand partner certificati (Anderson, Eurosup, Prolabs) e laboratori italiani." },
    { title: "Performante", desc: "Formule pensate per ogni livello: dalla persona attiva all'atleta di alto livello agonistico." },
    { title: "Elite", desc: "Standard professionali per chi vede la nutrizione come parte della propria disciplina quotidiana." },
  ],
};

const BrandStory = () => {
  const { settings } = useTheme();
  const tierNames = getTierNames(settings);
  const brand = { ...DEFAULT_BRAND, ...(settings?.brand || {}) };
  const pillars = Array.isArray(brand.pillars) && brand.pillars.length > 0 ? brand.pillars : DEFAULT_BRAND.pillars;
  return (
    <section
      id="brand"
      data-testid="brand-section"
      className="lv-section lv-section-brand relative py-24 md:py-36 overflow-hidden"
      style={{ backgroundColor: sectionBgColor(settings, "brand") }}
    >
      {/* Decorative silver lines */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#C0C0C0]/30 to-transparent" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="flex items-center gap-3"
            >
              <span className="h-px w-12 bg-[#C0C0C0]" />
              <span className="text-[11px] tracking-[0.38em] uppercase text-[#C0C0C0]" data-testid="brand-label">
                {brand.label}
              </span>
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="mt-6 font-[Outfit] text-white tracking-tight font-light leading-[1.05]"
              style={{ fontSize: "clamp(2.25rem, 4.5vw, 3.75rem)" }}
              data-testid="brand-title"
            >
              {brand.title}{" "}
              <span className="italic font-extralight text-[#C0C0C0]">
                {brand.title_accent}
              </span>
            </motion.h2>
          </div>

          <div className="lg:col-span-7 lg:pt-4">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="space-y-6 font-[Manrope] text-[#94A3B8] text-base sm:text-lg leading-relaxed"
            >
              <p data-testid="brand-paragraph1">{brand.paragraph1}</p>
              <p data-testid="brand-paragraph2">
                Tre linee, un solo standard: ogni prodotto in catalogo è scelto
                per coerenza con il livello a cui parla —{" "}
                <span className="text-[#E5E4E2]">{tierNames.start.name}</span>
                {" "}({tierNames.start.subtitle}),{" "}
                <span className="text-[#E5E4E2]">{tierNames.power.name}</span>
                {" "}({tierNames.power.subtitle}) e{" "}
                <span className="text-[#E5E4E2]">{tierNames.elite.name}</span>
                {" "}({tierNames.elite.subtitle}).
              </p>
            </motion.div>

            <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-6">
              {pillars.map((p, i) => {
                const Icon = PILLAR_ICONS[i % PILLAR_ICONS.length];
                return (
                  <motion.div
                    key={`${p.title}-${i}`}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.1 * i }}
                    data-testid={`pillar-${i}`}
                    className="group border-l border-[#C0C0C0]/20 pl-5 py-1 hover:border-[#C0C0C0]/60 transition-colors"
                  >
                    <Icon
                      className="w-5 h-5 text-[#C0C0C0] mb-3"
                      strokeWidth={1.5}
                    />
                    <h3 className="font-[Outfit] text-white text-xl tracking-tight mb-2">
                      {p.title}
                    </h3>
                    <p className="text-sm text-[#94A3B8] leading-relaxed">
                      {p.desc}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BrandStory;
